import { REALTIME_WS_URL } from "./config";

function readyStateLabel(socket) {
  if (!socket) {
    return "NO_SOCKET";
  }
  var map = {
    0: "CONNECTING",
    1: "OPEN",
    2: "CLOSING",
    3: "CLOSED",
  };
  return map[socket.readyState] || String(socket.readyState);
}

export function parseJson(value) {
  var current = value;
  for (var i = 0; i < 3; i++) {
    if (typeof current !== "string") {
      break;
    }
    try {
      current = JSON.parse(current);
    } catch (e) {
      break;
    }
  }
  return current;
}

/**
 * Shared Pusher-compatible WebSocket transport.
 * One connection, many channel subscriptions, many listeners.
 *
 * @param {{ url?: string }} [options]
 */
export function createRealtimeConnection(options) {
  options = options || {};
  var url = options.url || REALTIME_WS_URL;
  var socket = null;
  var pingTimer = null;
  var reconnectTimer = null;
  var closed = false;
  var channels = {};
  var listeners = [];

  function send(payload) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
    }
  }

  function subscribeAll() {
    Object.keys(channels).forEach(function (channel) {
      send({
        event: "pusher:subscribe",
        data: { channel: channel },
      });
    });
  }

  function startPing(ms) {
    clearInterval(pingTimer);
    pingTimer = setInterval(function () {
      send({ event: "pusher:ping", data: {} });
    }, ms);
  }

  function emitToListeners(parsed) {
    listeners.slice().forEach(function (fn) {
      try {
        fn(parsed);
      } catch (e) {
        // keep other listeners alive
      }
    });
  }

  function handlePacket(packet) {
    var eventName = packet && packet.event;
    if (!eventName) {
      return;
    }
    if (eventName === "pusher:connection_established") {
      var data = parseJson(packet.data) || {};
      var timeoutSec = Number(data.activity_timeout) || 30;
      subscribeAll();
      startPing(Math.max(10, timeoutSec - 5) * 1000);
      return;
    }
    if (eventName === "pusher:ping") {
      send({ event: "pusher:pong", data: {} });
      return;
    }
    if (
      eventName.indexOf("pusher:") === 0 ||
      eventName.indexOf("pusher_internal:") === 0
    ) {
      return;
    }
    emitToListeners({
      event: eventName,
      channel: packet.channel,
      data: parseJson(packet.data),
    });
  }

  function connect() {
    if (closed) {
      return;
    }
    try {
      socket = new WebSocket(url);
    } catch (e) {
      reconnectTimer = setTimeout(connect, 3000);
      return;
    }
    socket.onmessage = function (e) {
      var packet = parseJson(e.data);
      if (packet) {
        handlePacket(packet);
      }
    };
    socket.onclose = function () {
      clearInterval(pingTimer);
      if (!closed) {
        reconnectTimer = setTimeout(connect, 3000);
      }
    };
    socket.onerror = function () {
      if (socket) {
        socket.close();
      }
    };
  }

  connect();

  return {
    readyStateLabel: function () {
      return readyStateLabel(socket);
    },
    subscribe: function (channel) {
      if (!channel) {
        return;
      }
      if (channels[channel]) {
        return;
      }
      channels[channel] = true;
      send({
        event: "pusher:subscribe",
        data: { channel: channel },
      });
    },
    unsubscribe: function (channel) {
      if (!channel || !channels[channel]) {
        return;
      }
      delete channels[channel];
      send({
        event: "pusher:unsubscribe",
        data: { channel: channel },
      });
    },
    on: function (fn) {
      if (typeof fn !== "function") {
        return function () {};
      }
      listeners.push(fn);
      return function off() {
        var idx = listeners.indexOf(fn);
        if (idx !== -1) {
          listeners.splice(idx, 1);
        }
      };
    },
    disconnect: function () {
      closed = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      channels = {};
      listeners = [];
      if (socket) {
        socket.close();
        socket = null;
      }
    },
  };
}
